import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Check } from "lucide-react";
import { COUNTRIES, findCountry, flagEmoji } from "../data/countries";

// Phone input with searchable country-code dropdown.
// Props:
//  countryCode: ISO2 (default "KE")
//  phone: local number
//  onChange({ countryCode, phone, dial, e164 })
export default function PhoneInput({
  countryCode = "KE",
  phone = "",
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const country = findCountry(countryCode) || findCountry("KE");

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.includes(q.replace(/\+/g, ""))
    );
  }, [search]);

  const emit = (nextCode, nextPhone) => {
    const cc = findCountry(nextCode) || country;
    const digits = String(nextPhone || "").replace(/\D/g, "");
    onChange?.({
      countryCode: cc.code,
      phone: nextPhone,
      dial: cc.dial,
      e164: digits ? `+${cc.dial}${digits}` : "",
    });
  };

  const selectCountry = (code) => {
    setOpen(false);
    emit(code, phone);
  };

  return (
    <div ref={rootRef} className="relative">
      <div
        className={`flex items-stretch rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors duration-150 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20 ${
          open ? "border-indigo-500 ring-2 ring-indigo-500/20" : ""
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 rounded-l-lg border-r border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600"
          aria-label="Select country"
        >
          <span className="text-base leading-none">{flagEmoji(country.code)}</span>
          <span className="font-medium">+{country.dial}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 text-gray-500 dark:text-gray-400 transition-transform duration-150 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
        <input
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => emit(country.code, e.target.value)}
          placeholder="712 345 678"
          className="min-w-0 flex-1 rounded-r-lg bg-white dark:bg-gray-800 px-3 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none"
        />
      </div>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1.5 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg dark:shadow-black/40">
          <div className="border-b border-gray-100 dark:border-gray-700 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries…"
                className="w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 pl-8 pr-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
          </div>
          <ul className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">No countries found</li>
            )}
            {filtered.map((c) => {
              const selected = c.code === country.code;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => selectCountry(c.code)}
                    className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors duration-150 ${
                      selected ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300" : "text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <span className="text-base leading-none">{flagEmoji(c.code)}</span>
                    <span className="min-w-0 flex-1 truncate">{c.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">+{c.dial}</span>
                    {selected && <Check className="h-4 w-4 text-indigo-600" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
